/**
 * history.js
 * Server history modal with multi-scale navigation:
 *   DAY  → óránkénti pontok, ◀ ▶ napot léptet
 *   WEEK → napi átlagok, ◀ ▶ hetet léptet
 *   YEAR → heti átlagok, ◀ ▶ évet léptet
 */

import UPlot from 'uplot'
import { formatNumber } from './util'
import { uPlotTooltipPlugin } from './plugins'

const MS_PER_HOUR = 60 * 60 * 1000
const MS_PER_DAY = 24 * MS_PER_HOUR
const MS_PER_WEEK = 7 * MS_PER_DAY
const MS_PER_YEAR = 365 * MS_PER_DAY

const SCALES = ['day', 'week', 'year']

export class HistoryModal {
  constructor (app) {
    this._app = app
    this._visible = false
    this._currentIp = null
    this._currentName = null
    this._scale = 'day' // 'day' | 'week' | 'year'
    this._periodStart = null // epoch ms — kezdete az aktuálisan nézett periódusnak
    this._plotInstance = null
    this._iconCache = {} // ip -> [{timestamp, favicon_hash}]
    this._iconDataCache = {} // "ip:hash" -> dataUri
    this._historyCache = {} // "ip:from:to" -> rows

    this._bindDOM()
  }

  _bindDOM () {
    document.getElementById('history-modal-backdrop').addEventListener('click', () => this.hide())
    document.getElementById('history-modal-close').addEventListener('click', () => this.hide())
    document.getElementById('history-nav-prev').addEventListener('click', () => this._shift(-1))
    document.getElementById('history-nav-next').addEventListener('click', () => this._shift(+1))
    document.getElementById('history-date-label').addEventListener('click', () => this._cycleScale())

    document.addEventListener('keydown', (e) => {
      if (!this._visible) return
      if (e.key === 'Escape') this.hide()
      if (e.key === 'ArrowLeft') this._shift(-1)
      if (e.key === 'ArrowRight') this._shift(+1)
    })

    // Scale buttons
    SCALES.forEach(scale => {
      document.getElementById(`history-scale-${scale}`).addEventListener('click', () => {
        this._setScale(scale)
      })
    })
  }

  show (serverRegistration) {
    this._currentIp = serverRegistration.data.ip
    this._currentName = serverRegistration.data.name
    this._scale = 'day'
    this._periodStart = this._periodFloor(Date.now(), 'day')

    document.getElementById('history-modal-server-name').textContent = this._currentName
    document.getElementById('history-modal-server-ip').textContent = this._currentIp
    document.getElementById('history-modal').style.display = 'flex'
    this._visible = true

    this._updateScaleButtons()
    this._loadAndRender()
  }

  hide () {
    document.getElementById('history-modal').style.display = 'none'
    this._visible = false
    if (this._plotInstance) {
      this._plotInstance.destroy()
      this._plotInstance = null
    }
  }

  _cycleScale () {
    const idx = SCALES.indexOf(this._scale)
    this._setScale(SCALES[(idx + 1) % SCALES.length])
  }

  _setScale (scale) {
    this._scale = scale
    // Re-anchor periodStart to the same moment in the new scale
    this._periodStart = this._periodFloor(this._periodStart, scale)
    this._updateScaleButtons()
    this._loadAndRender()
  }

  _updateScaleButtons () {
    SCALES.forEach(scale => {
      const btn = document.getElementById(`history-scale-${scale}`)
      btn.classList.toggle('active', scale === this._scale)
    })
  }

  _periodFloor (ts, scale) {
    const d = new Date(ts)
    if (scale === 'day') {
      d.setHours(0, 0, 0, 0)
    } else if (scale === 'week') {
      // Floor to Monday
      const day = d.getDay() || 7 // 0=Sun → 7
      d.setDate(d.getDate() - (day - 1))
      d.setHours(0, 0, 0, 0)
    } else {
      // Floor to Jan 1
      d.setMonth(0, 1)
      d.setHours(0, 0, 0, 0)
    }
    return d.getTime()
  }

  _periodLength () {
    if (this._scale === 'day') return MS_PER_DAY
    if (this._scale === 'week') return MS_PER_WEEK
    return MS_PER_YEAR
  }

  _shift (delta) {
    const candidate = this._periodStart + delta * this._periodLength()
    if (candidate > this._periodFloor(Date.now(), this._scale)) return
    this._periodStart = candidate
    this._loadAndRender()
  }

  _periodLabel () {
    const d = new Date(this._periodStart)
    if (this._scale === 'day') {
      return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })
    } else if (this._scale === 'week') {
      const end = new Date(this._periodStart + MS_PER_WEEK - 1)
      const from = d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })
      const to = end.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })
      return `${d.getFullYear()} – ${from} → ${to}`
    } else {
      return `${d.getFullYear()}`
    }
  }

  async _loadAndRender () {
    const ip = this._currentIp
    const from = this._periodStart
    const to = from + this._periodLength() - 1

    // Update UI chrome
    document.getElementById('history-date-label').textContent = this._periodLabel()
    const isCurrent = this._periodStart >= this._periodFloor(Date.now(), this._scale)
    document.getElementById('history-nav-next').disabled = isCurrent

    this._updateIcon(ip, from + this._periodLength() / 2)

    // Fetch raw hourly rows for this window
    const cacheKey = `${ip}:${from}:${to}`
    let rows = this._historyCache[cacheKey]

    if (!rows) {
      try {
        const resp = await fetch(`/api/history?ip=${encodeURIComponent(ip)}&from=${from}&to=${to}`)
        rows = await resp.json()
        this._historyCache[cacheKey] = rows
      } catch (e) {
        rows = []
      }
    }

    // Aggregate if needed
    const points = this._aggregate(rows)

    this._renderChart(points)
    this._renderStats(points)

    const unitLabel = this._scale === 'day' ? 'óránkénti' : this._scale === 'week' ? 'napi' : 'heti'
    document.getElementById('history-range-info').textContent =
      `${points.length} ${unitLabel} adatpont`
  }

  /**
   * Aggregate raw hourly rows into the right bucket size for the current scale.
   * Returns [{timestamp (ms), playerCount}]
   */
  _aggregate (rows) {
    if (!rows || rows.length === 0) return []

    if (this._scale === 'day') {
      // Raw hourly — no aggregation needed
      return rows.map(r => ({ timestamp: r.timestamp, playerCount: r.playerCount }))
    }

    const bucketSize = this._scale === 'week' ? MS_PER_DAY : MS_PER_WEEK
    const buckets = {}

    for (const row of rows) {
      if (row.playerCount === null || row.playerCount === undefined) continue
      const bucket = Math.floor(row.timestamp / bucketSize) * bucketSize
      if (!buckets[bucket]) buckets[bucket] = []
      buckets[bucket].push(row.playerCount)
    }

    return Object.keys(buckets)
      .sort((a, b) => a - b)
      .map(bucket => ({
        timestamp: parseInt(bucket),
        playerCount: Math.round(buckets[bucket].reduce((a, b) => a + b, 0) / buckets[bucket].length)
      }))
  }

  async _updateIcon (ip, midTimestamp) {
    if (!this._iconCache[ip]) {
      try {
        const resp = await fetch(`/api/icons?ip=${encodeURIComponent(ip)}&from=0&to=${Date.now()}`)
        this._iconCache[ip] = await resp.json()
      } catch (e) {
        this._iconCache[ip] = []
      }
    }

    const iconTimeline = this._iconCache[ip] || []
    let activeIcon = null
    for (const entry of iconTimeline) {
      if (entry.timestamp <= midTimestamp) activeIcon = entry
    }

    const iconEl = document.getElementById('history-modal-icon')

    if (!activeIcon) {
      const sr = this._app.serverRegistry.getServerRegistrationByIp(ip)
      iconEl.src = (sr && sr.getFaviconUrl && sr.getFaviconUrl()) || '../images/missing_favicon.svg'
      return
    }

    const dataKey = `${ip}:${activeIcon.favicon_hash}`
    if (this._iconDataCache[dataKey]) {
      iconEl.src = this._iconDataCache[dataKey]
      return
    }

    try {
      const s = activeIcon.timestamp - 1
      const e = activeIcon.timestamp + 1
      const resp = await fetch(`/api/icons?ip=${encodeURIComponent(ip)}&from=${s}&to=${e}&full=1`)
      const fullRows = await resp.json()
      if (fullRows && fullRows[0] && fullRows[0].favicon_data) {
        this._iconDataCache[dataKey] = fullRows[0].favicon_data
        iconEl.src = fullRows[0].favicon_data
      }
    } catch (e) {
      iconEl.src = '../images/missing_favicon.svg'
    }
  }

  _renderChart (points) {
    const wrap = document.getElementById('history-chart-wrap')
    wrap.innerHTML = ''

    if (this._plotInstance) {
      this._plotInstance.destroy()
      this._plotInstance = null
    }

    if (!points || points.length === 0) {
      wrap.innerHTML = '<p class="history-no-data">Nincs adat erre az időszakra.</p>'
      return
    }

    const times = points.map(p => Math.floor(p.timestamp / 1000))
    const counts = points.map(p => p.playerCount)

    const tooltipFmt = (idx) => {
      const d = new Date(times[idx] * 1000)
      if (this._scale === 'day') {
        return d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })
      } else if (this._scale === 'week') {
        return d.toLocaleDateString('hu-HU', { weekday: 'long', month: 'short', day: 'numeric' })
      } else {
        // year: show week range
        const end = new Date(d.getTime() + MS_PER_WEEK - 1)
        return `${d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`
      }
    }

    this._plotInstance = new UPlot({
      plugins: [
        uPlotTooltipPlugin((pos, id) => {
          if (pos && typeof counts[id] === 'number') {
            this._app.tooltip.set(pos.left, pos.top, 10, 10,
              `${formatNumber(counts[id])} Játékos<br>${tooltipFmt(id)}`)
          } else {
            this._app.tooltip.hide()
          }
        })
      ],
      height: 180,
      width: Math.min(wrap.clientWidth || 560, 800),
      cursor: { y: false, drag: { setScale: false, x: false, y: false } },
      series: [
        {},
        {
          stroke: '#81a6e9ff',
          width: 2,
          value: (_, raw) => `${formatNumber(raw)} Játékos`,
          spanGaps: true,
          points: { show: points.length < 40 }
        }
      ],
      axes: [
        {
          font: '12px "Open Sans", sans-serif',
          stroke: '#A3A3A3',
          grid: { stroke: '#333', width: 1 }
        },
        {
          ticks: { show: false },
          font: '12px "Open Sans", sans-serif',
          stroke: '#A3A3A3',
          size: 55,
          grid: { stroke: '#333', width: 1 }
        }
      ],
      scales: { y: { auto: true } },
      legend: { show: false }
    }, [times, counts], wrap)
  }

  _renderStats (points) {
    if (!points || points.length === 0) {
      document.getElementById('history-stat-peak').textContent = '—'
      document.getElementById('history-stat-avg').textContent = '—'
      document.getElementById('history-stat-points').textContent = '0'
      return
    }

    const valid = points.map(p => p.playerCount).filter(v => v !== null && v !== undefined)
    const peak = valid.length ? Math.max(...valid) : null
    const avg = valid.length ? Math.round(valid.reduce((a, b) => a + b, 0) / valid.length) : null

    const peakLabel = { day: 'Napi csúcs', week: 'Heti csúcs', year: 'Évi csúcs' }[this._scale]
    const avgLabel = { day: 'Napi átlag', week: 'Heti átlag', year: 'Évi átlag' }[this._scale]

    document.getElementById('history-stat-peak-label').textContent = peakLabel
    document.getElementById('history-stat-avg-label').textContent = avgLabel

    document.getElementById('history-stat-peak').textContent = peak !== null ? formatNumber(peak) : '—'
    document.getElementById('history-stat-avg').textContent = avg !== null ? formatNumber(avg) : '—'
    document.getElementById('history-stat-points').textContent = points.length
  }
}

//
